import React, {
	createContext,
	useContext,
	useLayoutEffect,
	useMemo,
	useReducer,
	useEffect,
	useCallback,
	useRef,
} from 'react';

import {
	useMutation,
	useQuery,
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query';

import reducer, {initialState, type State} from './reducer';
import actions from './actions';
import {
	getAttributePermissions,
	type GetAttributePermissionsRequestOptions,
	type GetAttributePermissionsResult,
	setAttributePermissions,
	type SetAttributePermissionsResult,
	type SetAttributePermissionsRequestOptions,
	type QueryError,
} from '../api';

import type {AttributePermission} from '../types';

import {ACTIONS} from './constants';

import {useAttributeProvider} from '@netx/core-attribute/context/AttributesContext';

export type AttributePermissionContextType = {
	actions: ReturnType<typeof actions> & {
		edit(): void;
		endEdit(): void;
		save(): Promise<any>;
	};
	state: State;

	getAttributeTemplate(): ReturnType<
		ReturnType<typeof useAttributeProvider>['getById']
	>;
};

const queryClient = new QueryClient();

type QueryParams = {
	queryKey: [string, GetAttributePermissionsRequestOptions];
};

const reactQueryGetAttributePermissions = async (params: QueryParams) => {
	const [, queryParams] = params.queryKey;
	return getAttributePermissions(queryParams);
};

const StoreContext = createContext<AttributePermissionContextType | undefined>(
	undefined,
);

export const useAttributePermissionProvider = () => {
	const context = useContext(StoreContext);
	if (context === undefined) {
		throw new Error(
			'`useAttributePermissionProvider` must be used within a `AttributePermissionProvider`',
		);
	}
	return context;
};

export interface AttributePermissionProviderProps {
	templateId?: AttributePermission['templateId'];
}

const AttributePermissionProvider = ({
	children,
	templateId,
}: React.PropsWithChildren<AttributePermissionProviderProps>) => {
	const [state, dispatch] = useReducer(reducer, {...initialState, templateId});
	const attributesProvider = useAttributeProvider();

	// Hold onto a ref of the data
	// some of the methods here do not need to respond to data changes
	// but would like to access the current state of it when time
	const dataRef = useRef(state.data);
	dataRef.current = state.data;

	// When the `templateId` changes
	// `templateId` drives all the data fetching
	useEffect(() => {
		dispatch({type: ACTIONS.SET_TEMPLATE_ID, templateId});
	}, [templateId]);

	// MUTATION
	const attributePermissionsMutation = useMutation<
		SetAttributePermissionsResult,
		QueryError,
		SetAttributePermissionsRequestOptions
	>({
		mutationFn: setAttributePermissions,
		onError: (error) => {
			console.error(error);
		},
		onSuccess: (attributePermissions) => {
			dispatch({
				type: ACTIONS.UPDATE,
				data: attributePermissions.map((d, index) => ({...d, index})),
			});
		},
	});

	// QUERY
	const attributePermissionsQuery = useQuery<
		GetAttributePermissionsResult,
		QueryError,
		GetAttributePermissionsResult,
		QueryParams['queryKey']
	>({
		queryKey: [
			'getAttributePermissions',
			{templateId: state.templateId},
		] as QueryParams['queryKey'],
		queryFn: reactQueryGetAttributePermissions,
		enabled: state.templateId != null,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	useEffect(() => {
		dispatch({
			type: ACTIONS.UPDATE,
			data: (attributePermissionsQuery.data ?? []).map((d, index) => ({
				...d,
				index,
				id: d.templateId,
			})),
		});
	}, [attributePermissionsQuery.data, attributePermissionsQuery.dataUpdatedAt]);

	// The attribute context will handle showing the editor
	const edit = useCallback(() => {
		if (state.templateId == null) return;
		attributesProvider.setPermissionEdit(state.templateId);
	}, [state.templateId, attributesProvider.setPermissionEdit]);

	// The attribute context will handle showing the editor
	const endEdit = useCallback(() => {
		attributesProvider.setPermissionEdit(false);
	}, [attributesProvider.setPermissionEdit]);

	// Pass the request along to the attibute context
	const getAttributeTemplate = useCallback(
		() =>
			state.templateId != null
				? attributesProvider.getById(state.templateId)
				: undefined,
		[state.templateId, attributesProvider.getById],
	);

	// Save the current state of our data
	const save = useCallback(async () => {
		if (state.templateId == null) return;
		await attributePermissionsMutation.mutateAsync({
			templateId: state.templateId,
			// `templateId` is not needed in `groupAttributePermissions`
			groupAttributePermissions: dataRef.current.map(
				({templateId, ...rest}) => rest,
			),
		});
		endEdit();
	}, [endEdit, state.templateId]);

	// The reducer actions to expose to outside components
	const dispatchActions = useMemo(
		() => ({
			...actions(dispatch),
			edit,
			endEdit,
			save,
		}),
		[state.templateId, edit, endEdit, save],
	);

	// Reflect saving
	useLayoutEffect(() => {
		dispatch({
			type: ACTIONS.SET_SAVING,
			saving: attributePermissionsMutation.status === 'loading',
		});
	}, [attributePermissionsMutation.status]);

	// Reflect loading
	useLayoutEffect(() => {
		dispatch({
			type: ACTIONS.SET_LOADING,
			loading:
				attributePermissionsQuery.isLoading ||
				attributePermissionsQuery.isFetching,
		});
	}, [
		attributePermissionsQuery.isLoading,
		attributePermissionsQuery.isFetching,
	]);

	return (
		<StoreContext.Provider
			value={{
				actions: dispatchActions,
				state,

				getAttributeTemplate,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};

export default React.memo(
	(props: React.PropsWithChildren<AttributePermissionProviderProps>) => {
		return (
			<QueryClientProvider client={queryClient}>
				<AttributePermissionProvider {...props} />
			</QueryClientProvider>
		);
	},
);
